import { useCallback, useEffect, useRef, useState } from 'react'
import { useMediaQuery } from '@mui/material'

export default function useClippingMode () {
  const available = useMediaQuery('(min-width: 900px) and (pointer: fine)')
  const [active, setActive] = useState(false)
  const ownsFullscreen = useRef(false)
  const requested = useRef(false)

  const exit = useCallback(() => {
    requested.current = false
    setActive(false)
    if (ownsFullscreen.current && document.fullscreenElement === document.documentElement) {
      document.exitFullscreen().catch(() => {})
    }
    ownsFullscreen.current = false
  }, [])

  const enter = () => {
    if (!available) return
    requested.current = true
    setActive(true)
    // Fullscreen the document so body-portaled dialogs and menus remain usable.
    // A denied/unsupported request still provides a full-window workspace.
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      ownsFullscreen.current = true
      document.documentElement.requestFullscreen().then(() => {
        if (!requested.current) document.exitFullscreen().catch(() => {})
      }).catch(() => { ownsFullscreen.current = false })
    }
  }

  useEffect(() => {
    const changed = () => {
      if (ownsFullscreen.current && !document.fullscreenElement) exit()
    }
    document.addEventListener('fullscreenchange', changed)
    return () => {
      document.removeEventListener('fullscreenchange', changed)
      requested.current = false
      if (ownsFullscreen.current && document.fullscreenElement === document.documentElement) {
        document.exitFullscreen().catch(() => {})
      }
    }
  }, [exit])

  useEffect(() => { if (!available) exit() }, [available, exit])

  useEffect(() => {
    if (!active) return
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const escape = event => {
      if (event.key !== 'Escape') return
      // Video.js keeps hidden dialog elements mounted; only visible overlays own Escape.
      const overlayOpen = [...document.querySelectorAll('[role="dialog"], [role="listbox"], [role="menu"]')]
        .some(element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden')
      if (overlayOpen) return
      event.preventDefault()
      event.stopPropagation()
      exit()
    }
    window.addEventListener('keydown', escape, true)
    return () => {
      document.body.style.overflow = overflow
      window.removeEventListener('keydown', escape, true)
    }
  }, [active, exit])

  return { active, available, toggle: active ? exit : enter, exit }
}
